import React, { useCallback, useMemo } from 'react'
import { IconButton, makeStyles } from '@material-ui/core'
import { Tooltip, Modal, PageTitle } from '../'
import { CatchReplacedIcon } from '../catch-replaced-icon'
import { WarningReplacedIcon } from '../warning-replaced-icon'
import { useDispatch, useSelector } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { TableVirtualized } from '../table-virtualized/table'
import { NavLink } from 'react-router-dom'
import CameraIcon from '@material-ui/icons/PhotoCameraOutlined'
import { mdiMessageProcessingOutline, mdiTools } from '@mdi/js'
import Icon from '@mdi/react'
import {
    getFormattedDate,
    getReportTypeText,
    getReportTypeTooltipText
} from '../../../../../utils'
import ReportStatus from '../report-status'
import { REPORT_TYPES } from '../../../hooks/useReportTypes'

/* eslint-disable */
const useStyles = makeStyles(theme => ({
    root: {
        maxHeight: '90vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
    },
    pageTitle: {
        marginBottom: 40
    },
    link: {
        ...theme.link
    }
}))

export const StatisticsModal = ({ onClose, open, locationStatisticsData }) => {
    const dispatch = useDispatch()
    const classes = useStyles()

    const locale = useSelector(state => state.main.locale)
    const theme = useSelector(state => state.main.theme)
    const p = useSelector(state => state.main.p)

    const handleCancel = useCallback(() => {
        onClose()
    }, [onClose])

    const { date, devicesData, reportsData } = useMemo(() => {
        return {
            date: locationStatisticsData.payload.report_date,
            devicesData:
                locationStatisticsData.payload[
                    `${locationStatisticsData.dataKey}_devices`
                ].sorted,
            reportsData:
                locationStatisticsData.payload[
                    `${locationStatisticsData.dataKey}_reports`
                ]
        }
    }, [locationStatisticsData])

    const devicesColumns = useMemo(() => {
        return [
            {
                key: 'serial_number',
                label: locale.devices.serialNr,
                sortable: false,
                dataType: 'string',
                width: 1.5,
                render: ({ rowData }) => {
                    return (
                        <NavLink
                            onClick={e => e.stopPropagation()}
                            target={'_blank'}
                            to={`/devices/${rowData.id}`}
                            className={classes.link}
                        >
                            {rowData.serial_number}
                        </NavLink>
                    )
                }
            },
            {
                key: 'amount',
                label: locale.locations.reportsAmount,
                sortable: false,
                dataType: 'number',
                width: 1.5,
                alignCenter: true,
                render: ({ rowData }) => {
                    return <div>{rowData.amount}</div>
                }
            }
        ]
    }, [])

    const reportsColumns = [
        {
            key: 'name',
            label: locale.reports.deviceSerial,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            useCustomRenderer: false
        },
        {
            key: 'report_type',
            label: locale.reports.event,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            render: ({ cellData, rowData }) => {
                const catchDescription =
                    rowData.data && rowData.data.catch_description
                        ? rowData.data.catch_description
                        : null
                const hasTomPicture = !!rowData.tom_cam_picture_id
                return (
                    <div className={classes.reportTypeWrapper}>
                        {hasTomPicture && (
                            <CameraIcon className={classes.cameraIcon} />
                        )}
                        {getReportTypeText(rowData, locale)}
                        {catchDescription && (
                            <Tooltip
                                hover
                                placement="top"
                                text={rowData.data.catch_description}
                            />
                        )}
                        {rowData.has_comments ? (
                            <div style={{ marginLeft: '5px' }}>
                                <Tooltip
                                    hover
                                    placement="top"
                                    text={locale.reports.commentsAvailable}
                                >
                                    <Icon
                                        path={mdiMessageProcessingOutline}
                                        size={0.85}
                                    />
                                </Tooltip>
                            </div>
                        ) : null}
                    </div>
                )
            }
        },
        {
            key: 'created_at',
            label: locale.reports.time,
            sortable: false,
            dataType: 'date',
            width: 1.5,
            render: ({ cellData }) => <div>{getFormattedDate(cellData)}</div>
        },
        {
            key: 'status',
            label: locale.reports.status,
            sortable: false,
            dataType: 'number',
            alignCenter: true,
            width: 1,
            render: ({ rowData }) => {
                const reportTypeTooltipText = getReportTypeTooltipText(
                    locale,
                    rowData
                )
                const isStatusIconWithTooltip = reportTypeTooltipText !== null
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {rowData.in_maintenance_mode ? (
                            <Tooltip
                                hover
                                placement="top"
                                text={locale.common.maintenanceMode}
                            >
                                <Icon
                                    path={mdiTools}
                                    size={0.8}
                                    color={theme.primary}
                                />
                            </Tooltip>
                        ) : null}
                        {rowData.replaced_type === REPORT_TYPES.catch ? (
                            <>
                                <CatchReplacedIcon
                                    size={24}
                                    style={{ marginTop: -4 }}
                                />
                                →
                            </>
                        ) : null}
                        {rowData.replaced_type ===
                        REPORT_TYPES.false_triggering ? (
                            <>
                                <WarningReplacedIcon size={24} />→
                            </>
                        ) : null}
                        <Tooltip
                            hover
                            hideTooltip={!isStatusIconWithTooltip}
                            placement="top"
                            text={getReportTypeTooltipText(locale, rowData)}
                        >
                            <ReportStatus report={rowData} theme={theme} />
                        </Tooltip>
                    </div>
                )
            }
        }
    ]

    return (
        <Modal
            size="xl"
            onClose={handleCancel}
            open={open}
            className={classes.root}
        >
            <IconButton
                aria-label="close"
                onClick={onClose}
                style={{ position: 'absolute', top: 12, right: 12 }}
            >
                <CloseIcon />
            </IconButton>
            <PageTitle title={date} className={classes.pageTitle} />
            <div
                style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    marginBottom: 8,
                    letterSpacing: '-1.6px'
                }}
            >
                {locale.devices.devices}
            </div>
            <div style={{ marginTop: 12, marginBottom: 24 }}>
                <TableVirtualized
                    data={devicesData}
                    columns={devicesColumns}
                    height={300}
                />
            </div>
            <div
                style={{
                    fontSize: 20,
                    fontWeight: 'bold',
                    marginBottom: 8,
                    letterSpacing: '-1.6px'
                }}
            >
                {locale.reports.reports}
            </div>
            <div style={{ height: 300, marginTop: 12 }}>
                <TableVirtualized
                    data={reportsData}
                    columns={reportsColumns}
                    height={300}
                />
            </div>
        </Modal>
    )
}
/* eslint-enable */
